// SipDeviceLabelPdf.js
// Generates a 62x100mm label sticker PDF for SIP device configuration.
// Uses jsPDF for PDF generation and qrcode for QR code embedding.

import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

const ASTERISK_DEFAULTS = {
  server: 'phone.caro.care',
  tlsPort: 5061,
  udpPort: 5060,
  realm: 'caro.care',
  codec: 'G.711 \u03BC-law (PCMU)',
};

const CAMERA_STREAM_BASE = 'https://phone.caro.care/stream';

// CARO brand colors
const plumMain = [154, 63, 105];
const grey800 = [28, 37, 46];
const grey500 = [145, 158, 171];

/**
 * Generate a 62x100mm label sticker PDF for a SIP endpoint device.
 * @param {object} endpoint - Raw SIP endpoint data from the API
 */
export async function generateSipDeviceLabel(endpoint) {
  const transport = (endpoint.transport || 'tls').toLowerCase();
  const port = transport === 'tls' ? ASTERISK_DEFAULTS.tlsPort : ASTERISK_DEFAULTS.udpPort;
  const displayName = endpoint.displayName || endpoint.display_name || 'SIP Device';
  const hasCamera = endpoint.hasCamera && endpoint.deviceIp;
  const cameraEndpoint = endpoint.cameraEndpoint || `videophone-${endpoint.extension}`;

  // Create 62x100mm PDF (Brother QL large)
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [62, 100],
    compress: true,
  });

  const pageWidth = 62;
  const margin = 3;
  const contentWidth = pageWidth - margin * 2;
  let y = 0;

  // ─── Header bar (plum) ────────────────────────────────────────
  pdf.setFillColor(...plumMain);
  pdf.rect(0, 0, pageWidth, 10, 'F');

  try {
    const logoImg = new Image();
    logoImg.src = '/logo/logo-full.png';
    await new Promise((resolve) => {
      if (logoImg.complete) return resolve();
      logoImg.onload = resolve;
      logoImg.onerror = resolve;
      setTimeout(resolve, 800);
    });
    if (logoImg.complete && logoImg.naturalHeight > 0) {
      const h = 5;
      const w = (logoImg.width / logoImg.height) * h;
      pdf.addImage(logoImg, 'PNG', margin, 2.5, w, h);
    } else {
      throw new Error('fallback');
    }
  } catch {
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text('CARO', margin, 6.5);
  }

  y = 13;

  // ─── Device name + Extension ──────────────────────────────────
  pdf.setTextColor(...grey800);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(displayName, contentWidth - 20);
  pdf.text(nameLines, margin, y);
  y += nameLines.length * 3.2;

  pdf.setFontSize(12);
  pdf.setTextColor(...plumMain);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Ext: ${endpoint.extension}`, margin, y);
  y += 4.5;

  // Divider
  pdf.setDrawColor(...plumMain);
  pdf.setLineWidth(0.3);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 2.5;

  // ─── SIP Configuration ────────────────────────────────────────
  pdf.setFontSize(5.5);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...plumMain);
  pdf.text('SIP CONFIGURATION', margin, y);
  y += 2.8;

  const sipFields = [
    ['Server', ASTERISK_DEFAULTS.server],
    ['Port', String(port)],
    ['Transport', transport.toUpperCase()],
    ['Username', endpoint.extension],
    ['Password', endpoint.password || '(see Portal)'],
    ['Realm', ASTERISK_DEFAULTS.realm],
    ['Codec', ASTERISK_DEFAULTS.codec],
  ];

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(5);
  const labelColWidth = 15;
  for (const [label, value] of sipFields) {
    pdf.setTextColor(...grey500);
    pdf.text(label, margin, y);
    pdf.setTextColor(...grey800);
    pdf.setFont('helvetica', 'bold');
    const valLines = pdf.splitTextToSize(value, contentWidth - labelColWidth);
    pdf.text(valLines, margin + labelColWidth, y);
    pdf.setFont('helvetica', 'normal');
    y += valLines.length * 2.5;
  }

  // ─── Camera / RTSP Config (if applicable) ─────────────────────
  if (hasCamera) {
    y += 1.5;
    pdf.setFontSize(5.5);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...plumMain);
    pdf.text('CAMERA / RTSP', margin, y);
    y += 2.8;

    const streamUrl = `${CAMERA_STREAM_BASE}/${cameraEndpoint}/whep`;
    const cameraFields = [
      ['RTSP User', endpoint.extension],
      ['RTSP Pass', endpoint.password || '(same as SIP)'],
      ['Device IP', endpoint.deviceIp],
      ['Stream', streamUrl],
    ];

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(5);
    for (const [label, value] of cameraFields) {
      pdf.setTextColor(...grey500);
      pdf.text(label, margin, y);
      pdf.setTextColor(...grey800);
      pdf.setFont('helvetica', 'bold');
      const valLines = pdf.splitTextToSize(value, contentWidth - labelColWidth);
      pdf.text(valLines, margin + labelColWidth, y);
      pdf.setFont('helvetica', 'normal');
      y += valLines.length * 2.5;
    }
  }

  // ─── QR Code ──────────────────────────────────────────────────
  // Position QR consistently: push down if there's room, but don't overflow
  const qrBlockHeight = 24; // QR image + caption
  const footerY = 97;
  const qrStartY = Math.max(y + 2, footerY - qrBlockHeight - 2);

  const qrUrl = `${window.location.origin}/telephony?tab=devices&id=${endpoint.id}&type=endpoint`;

  try {
    const qrDataUrl = await QRCode.toDataURL(qrUrl, {
      width: 200,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#1C252E', light: '#FFFFFF' },
    });

    const qrSize = 18;
    const qrX = (pageWidth - qrSize) / 2;
    pdf.addImage(qrDataUrl, 'PNG', qrX, qrStartY, qrSize, qrSize);

    pdf.setFontSize(4);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...grey500);
    pdf.text('Scan to view full config in CARO Portal', pageWidth / 2, qrStartY + qrSize + 2, { align: 'center' });
  } catch (err) {
    // QR generation failed — skip silently
    console.warn('QR code generation failed:', err);
  }

  // ─── Footer ───────────────────────────────────────────────────
  pdf.setFontSize(3.5);
  pdf.setTextColor(...grey500);
  pdf.text(
    `CARO Technology \u00B7 Generated ${new Date().toLocaleDateString('en-GB')}`,
    pageWidth / 2,
    footerY,
    { align: 'center' }
  );

  // Save
  const safeName = displayName.replace(/[^a-zA-Z0-9 -]/g, '').replace(/\s+/g, '-');
  pdf.save(`SIP-Label-${endpoint.extension}-${safeName}.pdf`);
}
